import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";

export interface ILearnMoreSection {
  en_title: string;
  nl_title: string;
  en_bullets: string[];
  nl_bullets: string[];
}

export interface IHowItWorksTab {
  _id?: string;
  type: "how-it-works" | "what-to-drop" | "learn-more";
  en_tabTitle: string;
  nl_tabTitle: string;
  image?: string | null;
  sectionColor: string;
  en_steps: string[];
  nl_steps: string[];
  en_description: string;
  nl_description: string;
  en_details: string[];
  nl_details: string[];
  videos: string[];
  en_topTitle?: string;
  nl_topTitle?: string;
  learnMoreSections: ILearnMoreSection[];
  createdAt?: string;
  updatedAt?: string;
}

export interface IWhatToDropItem {
  _id?: string;
  key: string;
  en_title: string;
  nl_title: string;
  containerColor: string;
  panelColor: string;
  panelTextColor?: string;
  accentColor: string;
  itemsImageBgColor: string;
  en_price: string;
  nl_price: string;
  en_dos: string[];
  nl_dos: string[];
  en_donts: string[];
  nl_donts: string[];
  en_howText: string[];
  nl_howText: string[];
  en_whatHappensText: string;
  nl_whatHappensText: string;
  itemsImage?: string | null;
  methodImage?: string | null;
  whatHappensImage?: string | null;
  videoUrl?: string | null;
  linkedMaterialType?: string | null;
  order: number;
  isEnabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  tabs: IHowItWorksTab[];
  items: IWhatToDropItem[];
  isSaving: boolean;
  getTabs: () => Promise<void>;
  getItems: () => Promise<void>;
  updateTab: (type: string, body: Partial<IHowItWorksTab>) => Promise<boolean>;
  createItem: (body: Omit<IWhatToDropItem, "_id" | "createdAt" | "updatedAt">) => Promise<boolean>;
  updateItem: (id: string, body: Partial<IWhatToDropItem>) => Promise<boolean>;
  deleteItem: (id: string) => Promise<boolean>;
  reorderItems: (orderedIds: string[]) => Promise<void>;
}

export const useHowItWorks = create<IStore>()((set, get) => ({
  tabs: [],
  items: [],
  isSaving: false,

  getTabs: async () => {
    try {
      const res = await axios.get("how-it-works/tabs");
      if (res?.data) set({ tabs: res.data });
    } catch (error) {
      LOG.error("Error fetching how-it-works tabs", { error });
    }
  },

  getItems: async () => {
    try {
      const res = await axios.get("how-it-works/items");
      // Normalize en_howText/nl_howText to arrays — legacy rows stored them as
      // a single string before this field became a bullet list.
      const asArr = (v: unknown): string[] =>
        Array.isArray(v) ? (v as string[]) : v ? [v as string] : [];
      if (res?.data)
        set({
          items: (res.data as IWhatToDropItem[]).map((it) => ({
            ...it,
            en_howText: asArr(it.en_howText),
            nl_howText: asArr(it.nl_howText),
          })),
        });
    } catch (error) {
      LOG.error("Error fetching what-to-drop items", { error });
    }
  },

  updateTab: async (type, body) => {
    set({ isSaving: true });
    useAppLoading.getState().setIsLoading(true);
    try {
      const res = await axios.put(`how-it-works/tabs/${type}`, body);
      if (res?.data) {
        set((s) => ({
          tabs: s.tabs.map((t) => (t.type === type ? res.data : t)),
        }));
        return true;
      }
      return false;
    } catch (error) {
      LOG.error("Error updating how-it-works tab", { error });
      return false;
    } finally {
      set({ isSaving: false });
      useAppLoading.getState().setIsLoading(false);
    }
  },

  createItem: async (body) => {
    set({ isSaving: true });
    useAppLoading.getState().setIsLoading(true);
    try {
      const res = await axios.post("how-it-works/items", body);
      if (res?.data) {
        set((s) => ({ items: [...s.items, res.data].sort((a, b) => a.order - b.order) }));
        return true;
      }
      return false;
    } catch (error) {
      LOG.error("Error creating what-to-drop item", { error });
      return false;
    } finally {
      set({ isSaving: false });
      useAppLoading.getState().setIsLoading(false);
    }
  },

  updateItem: async (id, body) => {
    set({ isSaving: true });
    useAppLoading.getState().setIsLoading(true);
    try {
      const res = await axios.put(`how-it-works/items/${id}`, body);
      if (res?.data) {
        set((s) => ({
          items: s.items.map((it) => (it._id === id ? res.data : it)),
        }));
        return true;
      }
      return false;
    } catch (error) {
      LOG.error("Error updating what-to-drop item", { error });
      return false;
    } finally {
      set({ isSaving: false });
      useAppLoading.getState().setIsLoading(false);
    }
  },

  deleteItem: async (id) => {
    set({ isSaving: true });
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`how-it-works/items/${id}`);
      set((s) => ({ items: s.items.filter((it) => it._id !== id) }));
      return true;
    } catch (error) {
      LOG.error("Error deleting what-to-drop item", { error });
      return false;
    } finally {
      set({ isSaving: false });
      useAppLoading.getState().setIsLoading(false);
    }
  },

  reorderItems: async (orderedIds) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const res = await axios.put("how-it-works/items/reorder", { orderedIds });
      if (res?.data) set({ items: res.data });
    } catch (error) {
      LOG.error("Error reordering what-to-drop items", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
}));
